import {
  DISPLAY,
  GROUND,
  INK,
  LABEL_CLS,
  SURFACE_DARK,
  mix,
} from "@/components/editorial";
import { Check } from "lucide-react";
import Image from "next/image";
import { FEATURE_ROWS, type TwinPlan } from "./pricing-data";

const THIRD_PARTY_AI = [
  { name: "ChatGPT", icon: "chatgpt.svg" },
  { name: "Claude", icon: "claude.svg" },
  { name: "Gemini", icon: "gemini.svg" },
] as const;

const DEVELOPER_INTERFACES = ["CLI", "SDK", "API"] as const;

export function PricingPlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: TwinPlan;
  selected: boolean;
  onSelect: () => void;
}) {
  const includedFeatures = FEATURE_ROWS.map((row) => ({
    feature: row.feature,
    value: row[plan.id],
  }));

  return (
    <label
      className="relative flex min-h-[54rem] cursor-pointer flex-col border p-6 transition-[background-color,border-color,transform] duration-150 active:scale-[0.99]"
      style={{
        borderColor: selected ? SURFACE_DARK : mix("--theme-ink", 18),
        backgroundColor: selected ? SURFACE_DARK : GROUND,
        color: selected ? GROUND : INK,
      }}
    >
      <input
        type="radio"
        name="twin-plan"
        value={plan.id}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        className="absolute top-6 right-6 flex size-5 items-center justify-center border"
        style={{
          borderColor: selected
            ? "var(--theme-accent)"
            : mix("--theme-ink", 28),
          backgroundColor: selected ? "var(--theme-accent)" : "transparent",
        }}
      >
        {selected ? (
          <Check aria-hidden="true" className="size-3.5" style={{ color: INK }} />
        ) : null}
      </span>
      <span
        className={LABEL_CLS}
        style={{
          color: selected
            ? "var(--theme-accent)"
            : mix("--theme-ink", 66),
        }}
      >
        {plan.eyebrow}
      </span>
      <h3
        className="mt-8 text-[1.75rem] font-semibold tracking-[-0.025em]"
        style={{ ...DISPLAY, color: selected ? GROUND : INK }}
      >
        {plan.name}
      </h3>
      <p
        className="mt-3 text-[0.94rem] leading-[1.55]"
        style={{
          color: selected
            ? mix("--theme-ground", 82)
            : mix("--theme-ink", 82),
        }}
      >
        {plan.description}
      </p>
      <div className="mt-8">
        <p className="text-[1.25rem] font-semibold" style={DISPLAY}>
          {plan.price}
          {plan.cadence ? (
            <span
              className="ml-1 text-[0.8rem] font-normal"
              style={{
                color: selected
                  ? mix("--theme-ground", 64)
                  : mix("--theme-ink", 64),
              }}
            >
              {plan.cadence}
            </span>
          ) : null}
        </p>
        <span
          className="mt-6 flex min-h-11 w-full items-center justify-center px-4 text-center text-[0.78rem] font-semibold tracking-[0.08em] uppercase transition-transform duration-150 active:scale-[0.97]"
          style={{
            backgroundColor: selected ? "var(--theme-accent)" : SURFACE_DARK,
            color: selected ? INK : GROUND,
          }}
        >
          {selected ? `${plan.name} selected` : `Select ${plan.name}`}
        </span>
      </div>

      <div
        className="mt-6 border-t pt-5"
        style={{
          borderColor: selected
            ? mix("--theme-ground", 18)
            : mix("--theme-ink", 14),
        }}
      >
        <p
          className={LABEL_CLS}
          style={{
            color: selected
              ? mix("--theme-ground", 62)
              : mix("--theme-ink", 66),
          }}
        >
          Included in this plan
        </p>
        <ul className="mt-5 space-y-4">
          {includedFeatures.map((item) => (
            <li
              key={item.feature}
              className="flex items-start gap-2.5 text-[0.78rem] leading-[1.35]"
            >
              <Check
                aria-hidden="true"
                className="mt-0.5 size-3.5 shrink-0"
                style={{
                  color: selected ? "var(--theme-accent)" : SURFACE_DARK,
                }}
              />
              <span>
                <span className="block font-medium">{item.feature}</span>
                <span
                  className="mt-0.5 block"
                  style={{
                    color: selected
                      ? mix("--theme-ground", 62)
                      : mix("--theme-ink", 66),
                  }}
                >
                  {item.value}
                </span>
                {item.feature === "Third-party AI and developer access" ? (
                  <span className="mt-2.5 block space-y-2">
                    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                      {THIRD_PARTY_AI.map((surface) => (
                        <span
                          key={surface.name}
                          className="inline-flex items-center gap-1.5"
                        >
                          <Image
                            src={`/concepts/source-icons/${surface.icon}`}
                            width={17}
                            height={17}
                            alt=""
                            className="size-[17px] shrink-0 object-contain"
                          />
                          <span className="text-[0.66rem] font-medium">
                            {surface.name}
                          </span>
                        </span>
                      ))}
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      {DEVELOPER_INTERFACES.map((surface) => (
                        <span
                          key={surface}
                          className="inline-flex min-h-5 items-center border px-1.5 text-[0.6rem] font-semibold tracking-[0.08em]"
                          style={{
                            borderColor: selected
                              ? mix("--theme-ground", 28)
                              : mix("--theme-ink", 24),
                            color: selected
                              ? mix("--theme-ground", 82)
                              : mix("--theme-ink", 76),
                          }}
                        >
                          {surface}
                        </span>
                      ))}
                    </span>
                  </span>
                ) : null}
                {item.feature === "Product support" && plan.id !== "core" ? (
                  <span className="mt-2 flex flex-wrap items-center gap-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <Image
                        src="/concepts/source-icons/slack.png"
                        width={18}
                        height={18}
                        alt=""
                        className="size-[18px] shrink-0 object-contain"
                      />
                      <span className="text-[0.7rem] font-medium">Slack</span>
                    </span>
                    <span
                      className="text-[0.65rem]"
                      style={{
                        color: selected
                          ? mix("--theme-ground", 60)
                          : mix("--theme-ink", 66),
                      }}
                    >
                      or
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Image
                        src="/concepts/source-icons/teams.svg"
                        width={18}
                        height={18}
                        alt=""
                        className="size-[18px] shrink-0 object-contain"
                      />
                      <span className="text-[0.7rem] font-medium">Teams</span>
                    </span>
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto pt-10">
        <div
          className="border-t pt-5"
          style={{
            borderColor: selected
              ? mix("--theme-ground", 18)
              : mix("--theme-ink", 14),
          }}
        >
          <p
            className="text-[0.75rem] leading-[1.45]"
            style={{
              color: selected
                ? mix("--theme-ground", 62)
                : mix("--theme-ink", 66),
            }}
          >
            {plan.detail}
          </p>
        </div>
      </div>
    </label>
  );
}
